import { useState, useCallback } from 'react'
import { AuthProvider } from './context/AuthContext'
import { useAuth } from './hooks/useAuth'
import { CollectionProvider } from './context/CollectionContext'
import { SocialProvider } from './context/SocialContext'
import { useCollection } from './hooks/useCollection'
import Header from './components/Header'
import HeaderStatsBand from './components/HeaderStatsBand'
import MobileHeader from './components/MobileHeader'
import BottomTabBar from './components/BottomTabBar'
import Dashboard from './components/Dashboard'
import ProfileView from './components/ProfileView'
import AuthPage from './components/AuthPage'
import SeriesGrid from './components/SeriesGrid'
import SetGrid from './components/SetGrid'
import CardGrid from './components/CardGrid'
import CardModal from './components/CardModal'
import WishlistView from './components/WishlistView'
import PokedexBinder from './components/PokedexBinder'
import FriendsView from './components/FriendsView'
import TradesView from './components/TradesView'
import TradeProposalBuilder from './components/TradeProposalBuilder'
import LocalStorageMigration from './components/LocalStorageMigration'

const COLLECTION_VIEWS = ['series', 'sets', 'cards']

// Main app component that uses the collection context
function AppContent() {
  const [currentView, setCurrentView] = useState('dashboard')
  const [selectedSeries, setSelectedSeries] = useState(null)
  const [selectedCard, setSelectedCard] = useState(null)

  const { loadSetCards, currentSet, setCurrentSet, setCards } = useCollection()

  const handleSeriesSelect = useCallback((series) => {
    setSelectedSeries(series)
    setCurrentView('sets')
  }, [])

  const handleBackToSeries = useCallback(() => {
    setCurrentView('series')
    setSelectedSeries(null)
    setCurrentSet(null)
    setCards([])
  }, [setCurrentSet, setCards])

  // Handle set selection
  const handleSetSelect = useCallback(async (set) => {
    try {
      await loadSetCards(set)
      setCurrentView('cards')
    } catch (error) {
      console.error('Error loading set:', error)
      alert(`Error loading ${set.name}: ${error.message}`)
    }
  }, [loadSetCards])

  const handleBackToSets = useCallback(() => {
    if (!selectedSeries && currentSet?.series) {
      setSelectedSeries(currentSet.series);
    }
    setCurrentView('sets');
  }, [selectedSeries, currentSet])

  // Handle card selection
  const handleCardSelect = useCallback((card) => {
    setSelectedCard(card)
  }, [])

  // Handle card modal close
  const handleCardModalClose = useCallback(() => {
    setSelectedCard(null)
  }, [])

  // Shared nav dispatcher for desktop Header tabs + mobile BottomTabBar.
  // 'series' always resets the collection drill-down back to the top.
  const handleNavigate = useCallback((target) => {
    if (target === 'series') {
      handleBackToSeries()
      return
    }
    setCurrentView(target)
  }, [handleBackToSeries])

  // GlobalSearch success handler — explicitly switches to the cards view for
  // the found card's set. Replaces the old implicit auto-nav effect that
  // watched currentSet changing, which broke once views beyond series/cards existed.
  const handleSearchNavigate = useCallback((set) => {
    setSelectedSeries(set.series)
    setCurrentView('cards')
  }, [])

  const showCollectionChrome = COLLECTION_VIEWS.includes(currentView)

  return (
    <div className="min-h-screen bg-cream-bg">
      <Header currentView={currentView} onNavigate={handleNavigate} />
      {showCollectionChrome && <HeaderStatsBand onSearchNavigate={handleSearchNavigate} />}
      <MobileHeader />

      <div className="pb-20 md:pb-0">
        {currentView === 'dashboard' && (
          <Dashboard onNavigate={handleNavigate} onSearchNavigate={handleSearchNavigate} />
        )}

        {currentView === 'series' && (
          <SeriesGrid onSeriesSelect={handleSeriesSelect} />
        )}

        {currentView === 'sets' && (
          <SetGrid
            selectedSeries={selectedSeries}
            onBack={handleBackToSeries}
            onSetSelect={handleSetSelect}
          />
        )}

        {currentView === 'cards' && (
          <>
            <CardGrid
              onBack={handleBackToSets}
              onCardSelect={handleCardSelect}
            />

            {selectedCard && (
              <CardModal
                card={selectedCard}
                onClose={handleCardModalClose}
              />
            )}
          </>
        )}

        {currentView === 'pokedex' && <PokedexBinder />}

        {currentView === 'wishlist' && <WishlistView />}

        {currentView === 'trades' && <TradesView />}

        {currentView === 'friends' && <FriendsView />}

        {currentView === 'profile' && <ProfileView />}
      </div>

      <BottomTabBar currentView={currentView} onNavigate={handleNavigate} />

      {/* Trade proposal builder overlay — opened via SocialContext from
          match cards, the Proposals tab, or CardModal's Offer Trade */}
      <TradeProposalBuilder />
    </div>
  )
}

// Auth gate — shows login or app content
function AuthGate() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-cream-bg flex items-center justify-center">
        <div className="flex items-center gap-3">
          <div className="animate-spin rounded-full h-6 w-6 border-2 border-navy-dark border-t-transparent"></div>
          <span className="text-ink-secondary">Loading...</span>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <AuthPage />;
  }

  return (
    <CollectionProvider>
      <SocialProvider>
        <LocalStorageMigration />
        <AppContent />
      </SocialProvider>
    </CollectionProvider>
  );
}

// Root App component with provider
function App() {
  return (
    <AuthProvider>
      <AuthGate />
    </AuthProvider>
  )
}

export default App
